// apps/client/src/components/XeroSyncStatus.tsx
import { JobStatus, XeroSyncPhase, XeroSyncType } from '@avanzu/types';
import { Cancel as CancelIcon, CheckCircle, Close, Error as ErrorIcon } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useXeroSync } from '../contexts/QueueContext';
import { classifyXeroSyncError } from '../utils/xeroSyncError';

const getSyncTypeLabel = (syncType: XeroSyncType): string => {
  switch (syncType) {
    case XeroSyncType.ACCOUNTS:
      return 'Accounts';
    case XeroSyncType.ASSETS:
      return 'Assets';
    case XeroSyncType.EMPLOYEES:
      return 'Employees';
    case XeroSyncType.PAYSLIPS:
      return 'Payslips';
    case XeroSyncType.TRANSACTIONS:
      return 'Transactions';
    case XeroSyncType.BILLS:
      return 'Bills';
    case XeroSyncType.SELF_INVOICES:
      return 'Invoices';
    default:
      return syncType;
  }
};

/**
 * In-progress label. The transactions sync runs several sub-steps (bank
 * transactions → bills → self-invoices) and reports the live `phase`, so we
 * name what's actually running rather than the static sync-type label. Each
 * phase clears documents deleted in Xero as its first step, grouped under that
 * phase's label. Other sync types have no phase and fall back.
 */
const getInProgressLabel = (
  phase: XeroSyncPhase | null | undefined,
  syncLabel: string
): string => {
  switch (phase) {
    case XeroSyncPhase.TRANSACTIONS:
      return 'Syncing bank transactions...';
    case XeroSyncPhase.BILLS:
      return 'Syncing bills...';
    case XeroSyncPhase.SELF_INVOICES:
      return 'Syncing invoices...';
    default:
      return `Syncing ${syncLabel}...`;
  }
};

interface XeroSyncStatusProps {
  sidebarOpen: boolean;
}

export const XeroSyncStatus: React.FC<XeroSyncStatusProps> = ({ sidebarOpen }) => {
  const { currentJob, clearSync, cancelSync } = useXeroSync();
  const [isHovered, setIsHovered] = useState(false);

  if (!currentJob) {
    return null;
  }

  const { syncType, progress, total, status, errorMessage, deleted, phase } = currentJob;
  const syncLabel = getSyncTypeLabel(syncType);
  const inProgressLabel = getInProgressLabel(phase, syncLabel);
  const progressPercent = total > 0 ? Math.round((progress / total) * 100) : 0;

  const isInProgress =
    status === JobStatus.IN_PROGRESS || status === JobStatus.QUEUED;
  const isComplete = status === JobStatus.COMPLETED;
  const isFailed = status === JobStatus.FAILED;

  const handleCancel = async () => {
    await cancelSync();
  };

  // Collapsed view - just show circular progress centered
  if (!sidebarOpen) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
          ml: 2,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Tooltip title={inProgressLabel} placement="right">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isComplete ? 'success.light' : isFailed ? 'error.light' : 'action.hover',
              cursor: isInProgress ? 'pointer' : 'default',
              transition: 'all 0.2s',
              ...(isInProgress && {
                '&:hover': {
                  bgcolor: 'error.light',
                },
              }),
            }}
            onClick={isInProgress ? handleCancel : undefined}
          >
            {isInProgress && !isHovered && (
              <CircularProgress
                variant={total > 0 ? 'determinate' : 'indeterminate'}
                value={total > 0 ? progressPercent : undefined}
                size={24}
              />
            )}
            {isInProgress && isHovered && <CancelIcon sx={{ fontSize: 24, color: 'error.main' }} />}
            {isComplete && <CheckCircle sx={{ fontSize: 24, color: 'success.main' }} />}
            {isFailed && <ErrorIcon sx={{ fontSize: 24, color: 'error.main' }} />}
          </Box>
        </Tooltip>
      </Box>
    );
  }

  // Expanded view - show full details
  return (
    <Box sx={{ px: 1, py: 2 }}>
      <Box
        sx={{ p: 1.5 }}
        // elevation={2}
        // sx={{
        //   p: 1.5,
        //   borderRadius: 2,
        //   bgcolor: 'background.paper',
        // }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Stack spacing={1.5}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              {isInProgress && !isHovered && <CircularProgress size={16} />}
              {isInProgress && isHovered && (
                <Tooltip title="Click to cancel">
                  <IconButton size="small" onClick={handleCancel} sx={{ p: 0 }}>
                    <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                  </IconButton>
                </Tooltip>
              )}
              {isComplete && <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />}
              {isFailed && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
              <Typography variant="caption" fontWeight={600}>
                {isInProgress && inProgressLabel}
                {isComplete && `${syncLabel} Synced`}
                {isFailed && `Sync Failed`}
              </Typography>
            </Stack>
            {(isComplete || isFailed) && (
              <IconButton size="small" onClick={clearSync} sx={{ ml: 0.5 }}>
                <Close fontSize="small" />
              </IconButton>
            )}
          </Stack>

          {/* Progress bar and details */}
          {isInProgress && (
            <>
              {total > 0 ? (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{ height: 4, borderRadius: 2 }}
                  />
                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                    {progress} / {total} ({progressPercent}%)
                  </Typography>
                </>
              ) : (
                <>
                  <LinearProgress variant="indeterminate" sx={{ height: 4, borderRadius: 2 }} />
                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                    Starting...
                  </Typography>
                </>
              )}
            </>
          )}

          {/* Success message */}
          {isComplete && (total > 0 || (deleted ?? 0) > 0) && (
            <Typography variant="caption" color="success.main" fontSize="0.7rem">
              Synced {total} {syncLabel.toLowerCase()}
              {(deleted ?? 0) > 0 && `, removed ${deleted} deleted in Xero`}
            </Typography>
          )}

          {/* Error message */}
          {isFailed && errorMessage && (
            <Typography variant="caption" color="error.main" fontSize="0.7rem">
              {classifyXeroSyncError(errorMessage).display}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default XeroSyncStatus;
